import React, { useState } from "react";
import PaginationAtom from "../../atoms/pagination";
import styles from "./Pagination.module.css";
import {
  LabelPairedChevronLeftSmBoldIcon,
  LabelPairedChevronLeftSmRegularIcon,
  LabelPairedChevronRightSmRegularIcon,
} from "@deriv/quill-icons";

export type PaginationVariant = "number" | "bullet";

export interface PaginationProps<T> {
  data: T[];
  itemsPerPage?: number;
  currentPage?: number; // controlled
  defaultPage?: number; // uncontrolled
  onPageChange?: (page: number) => void;
  variant?: PaginationVariant;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  itemsClassName?: string;
}

export const Pagination = <T,>({
  data,
  itemsPerPage = 10,
  currentPage,
  defaultPage = 1,
  onPageChange,
  variant = "number",
  renderItem,
  className = "",
  itemsClassName = "",
}: PaginationProps<T>) => {
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const isControlled = currentPage !== undefined;
  const [internalPage, setInternalPage] = useState(defaultPage);
  const page = isControlled ? currentPage! : internalPage;

  const handlePageChange = (newPage: number) => {
    if (!isControlled) setInternalPage(newPage);
    onPageChange?.(newPage);
  };

  const startIdx = (page - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const pageData = data.slice(startIdx, endIdx);

  const getPageButtons = () => {
    if (variant === "bullet" || totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [];
    if (page <= 3) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (page >= totalPages - 2) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
    }
    return pages;
  };

  return (
    <div
      className={[styles.root, className, "quill-ui-next-component"]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={[styles.items, itemsClassName].filter(Boolean).join(" ")}>
        {pageData.map((item, idx) => renderItem(item, startIdx + idx))}
      </div>
      <div className={styles.controls}>
        <PaginationAtom
          variant="button"
          icon={<LabelPairedChevronLeftSmRegularIcon />}
          state={page === 1 ? "disabled" : "default"}
          onClick={page > 1 ? () => handlePageChange(page - 1) : undefined}
        />
        {getPageButtons().map((btn, idx) => {
          if (btn === '...') {
            if (variant !== 'number') return null;
            return (
              <PaginationAtom
                key={`ellipsis-${idx}`}
                variant="page"
                label={"..."}
                state={"disabled"}
                onClick={undefined}
              />
            );
          }
          const pageNum = btn as number;
          return (
            <PaginationAtom
              key={pageNum}
              variant={variant === "number" ? "page" : variant}
              label={variant === "number" ? String(pageNum) : undefined}
              state={page === pageNum ? "selected" : "default"}
              onClick={page !== pageNum ? () => handlePageChange(pageNum) : undefined}
            />
          );
        })}
        <PaginationAtom
          variant="button"
          icon={<LabelPairedChevronRightSmRegularIcon />}
          state={page === totalPages ? "disabled" : "default"}
          onClick={
            page < totalPages ? () => handlePageChange(page + 1) : undefined
          }
        />
      </div>
    </div>
  );
};

Pagination.displayName = "Pagination";

export default Pagination;
